import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { apiRequest } from '../api/client'
import type { DictPath } from '../i18n/translate'
import { useAuthStore } from '../stores/auth'
import { useWorkspaceStore } from '../stores/workspace'
import type {
    AddMemberResponse,
    Address,
    UserProfile,
    Workspace,
    WorkspaceInvite,
    WorkspaceMember,
    WorkspaceRole,
} from '../types/api'

// ---------- Mitglieder & Einladungen ----------

export function useMembers(workspaceId: number | null) {
    return useQuery({
        queryKey: ['members', workspaceId],
        enabled: workspaceId !== null,
        queryFn: async () => {
            const res = await apiRequest<{ data: WorkspaceMember[] }>(
                `/workspaces/${workspaceId}/members`,
            )
            return res.data
        },
    })
}

export function useMemberMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () => {
        void queryClient.invalidateQueries({
            queryKey: ['members', workspaceId],
        })
        void queryClient.invalidateQueries({
            queryKey: ['invites', workspaceId],
        })
        // Vertragsdaten des Mitglieds ändern die Arbeitszeit-Matrix
        void queryClient.invalidateQueries({
            queryKey: ['workingTimeMonthly', workspaceId],
        })
    }

    /** 201 {added:true, member} | 202 {added:false, invite} */
    const add = useMutation({
        mutationFn: (body: { email: string; role?: 'ADMIN' | 'MEMBER' }) =>
            apiRequest<AddMemberResponse>(`/workspaces/${workspaceId}/members`, {
                method: 'POST',
                body,
            }),
        onSuccess: invalidate,
    })

    /** leerer Body → 400; letzten OWNER demoten → 400 (Backend prüft).
     * Arbeitszeit-Vertragsdaten (Setup) trägt das Mitglied selbst oder
     * OWNER/ADMIN ein; Vorträge nur OWNER/ADMIN (Backend prüft). */
    const update = useMutation({
        mutationFn: ({
            userId,
            ...body
        }: { userId: number } & Partial<
            Pick<
                WorkspaceMember,
                | 'role'
                | 'hourlyRate'
                | 'weeklyTargetHours'
                | 'coreTimeFrom'
                | 'coreTimeTo'
                | 'vacationDaysPerYear'
                | 'workingTimeStart'
                | 'autoBreak'
                | 'overtimeCarriedHours'
                | 'vacationCarriedDays'
            >
        >) =>
            apiRequest<WorkspaceMember>(
                `/workspaces/${workspaceId}/members/${userId}`,
                { method: 'PATCH', body },
            ),
        onSuccess: invalidate,
    })

    const remove = useMutation({
        mutationFn: (userId: number) =>
            apiRequest<void>(`/workspaces/${workspaceId}/members/${userId}`, {
                method: 'DELETE',
            }),
        onSuccess: invalidate,
    })

    return { add, update, remove }
}

export function useInvites(workspaceId: number | null) {
    return useQuery({
        queryKey: ['invites', workspaceId],
        enabled: workspaceId !== null,
        queryFn: async () => {
            const res = await apiRequest<{ data: WorkspaceInvite[] }>(
                `/workspaces/${workspaceId}/invites`,
            )
            return res.data
        },
    })
}

export function useRevokeInvite(workspaceId: number | null) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (inviteId: number) =>
            apiRequest<void>(
                `/workspaces/${workspaceId}/invites/${inviteId}`,
                { method: 'DELETE' },
            ),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['invites', workspaceId],
            }),
    })
}

// ---------- Workspace (Name / UID) ----------

/** PATCH /workspaces/:id — danach Profil neu laden (Workspace-Switcher). */
export function useUpdateWorkspace(workspaceId: number | null) {
    const setUser = useAuthStore(s => s.setUser)
    return useMutation({
        mutationFn: (body: { name?: string; taxNumber?: string | null }) =>
            apiRequest<Workspace>(`/workspaces/${workspaceId}`, {
                method: 'PATCH',
                body,
            }),
        onSuccess: async () => {
            const profile = await apiRequest<UserProfile>('/users/profile')
            setUser(profile)
        },
    })
}

// ---------- Workspace löschen (WP4b: Lösch-Code per Mail) ----------

/**
 * 3-Schritte-Löschflow: (1) Name bestätigen, (2) 6-stelligen Code per Mail
 * anfordern (15 Min gültig, single-use), (3) DELETE mit Code. Nach Erfolg
 * Profil neu laden (Membership fällt weg), lokale Workspace-Wahl zurücksetzen
 * und alle Queries räumen — der Fallback (erste Membership/Onboarding) greift.
 */
export function useWorkspaceDelete(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const setUser = useAuthStore(s => s.setUser)
    const resetWorkspaceStore = useWorkspaceStore(s => s.reset)

    const requestCode = useMutation({
        mutationFn: () =>
            apiRequest<void>(`/workspaces/${workspaceId}/request-delete-token`, {
                method: 'POST',
            }),
    })

    const confirmDelete = useMutation({
        mutationFn: (deleteToken: string) =>
            apiRequest<void>(`/workspaces/${workspaceId}`, {
                method: 'DELETE',
                body: { deleteToken },
            }),
        onSuccess: async () => {
            resetWorkspaceStore()
            queryClient.removeQueries()
            const profile = await apiRequest<UserProfile>('/users/profile')
            setUser(profile)
        },
    })

    return { requestCode, confirmDelete }
}

// ---------- Adressen ----------

export function useAddresses(workspaceId: number | null) {
    return useQuery({
        queryKey: ['addresses', workspaceId],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<Address[]>(`/addresses?workspaceId=${workspaceId}`),
    })
}

export interface AddressBody {
    street: string
    streetNumber: string
    additionalInfo?: string
    postalCode: string
    city: string
    country: string
    isDefault?: boolean
}

export function useAddressMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () =>
        queryClient.invalidateQueries({
            queryKey: ['addresses', workspaceId],
        })

    const create = useMutation({
        mutationFn: (body: AddressBody) =>
            apiRequest<Address>('/addresses', {
                method: 'POST',
                body: { workspaceId, ...body },
            }),
        onSuccess: invalidate,
    })

    const update = useMutation({
        mutationFn: ({ id, ...body }: { id: number } & AddressBody) =>
            apiRequest<Address>(`/addresses/${id}`, {
                method: 'PATCH',
                body,
            }),
        onSuccess: invalidate,
    })

    const remove = useMutation({
        mutationFn: (id: number) =>
            apiRequest<void>(`/addresses/${id}`, { method: 'DELETE' }),
        onSuccess: invalidate,
    })

    return { create, update, remove }
}

/** i18n-Keys für Rollen — Anzeigestellen rendern t(workspaceRoleLabels[role]). */
export const workspaceRoleLabels: Record<WorkspaceRole, DictPath> = {
    OWNER: 'workspace.roles.owner',
    ADMIN: 'workspace.roles.admin',
    MEMBER: 'workspace.roles.member',
}
